import { useState, useEffect, useRef } from "react";
import { Form, Input, Button, Checkbox, Typography, Alert, Tooltip } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import Turnstile from "react-turnstile";
import { api } from "../../services/api";
import { resolveImgUrl } from "../../templates/4/utils";
import { useAuth } from "../../context/AuthContext";
import { useClient } from "../../context/ClientContext";
import { useNavigate, useLocation } from "react-router-dom";
import './style.css';
import { TURNSTILE_SITEKEY } from '../../services/config';

const { Title, Text } = Typography;

export const LoginForm = () => {
  const [email, setEmail]       = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [step, setStep]         = useState(1);
  const [otp, setOtp]           = useState("");
  const [trusted, setTrusted]   = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaKey, setCaptchaKey]     = useState(0);
  const [nombre, setNombre]     = useState("");
  const [apellido, setApellido] = useState("");
  const [telefono, setTelefono] = useState("");
  const [clientes, setClientes] = useState([]);

  const captchaRef = useRef();
  const { clientId, config , clientPath} = useClient();
  const { setUser, login, selectCliente } = useAuth();
  const navigate = useNavigate();
  // A dónde volver después del login: lo deja quien mandó a loguearse (por
  // ejemplo, al querer agregar al carrito). Viene del state del router y no de
  // la URL, así que no sirve para redirigir a otro sitio.
  const volverA = useLocation().state?.volverA;

  const loginLogoRaw = config?.logo?.login || config?.logo?.desktop || null;
  const loginLogo = loginLogoRaw ? resolveImgUrl(loginLogoRaw) : null;
  const loginBg = config?.loginBg ? resolveImgUrl(config.loginBg) : null;

  useEffect(() => {
    let id = localStorage.getItem("deviceId");
    if (!id) { id = crypto.randomUUID(); localStorage.setItem("deviceId", id); }
    setDeviceId(id);
  }, []);

  useEffect(() => {
    if (resendTimer > 0) {
      const t = setTimeout(() => setResendTimer(r => r - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendTimer]);

  const handleSendCode = async () => {
    setLoading(true); setError("");
    try {
      const res = await api.auth.sendCode({ email, deviceId, captcha: captchaToken, tenantId: clientId });
      if (res.status === "success") { setStep(2); setResendTimer(60); }
      else {
        setError(res.message || "No se pudo enviar el código.");
        if (captchaRef.current) captchaRef.current.reset();
        setCaptchaToken("");
      }
    } catch { setError("Error de red o servidor."); }
    setLoading(false);
  };

  const handleVerifyCode = async () => {
    setLoading(true); setError("");
    try {
      const res = await api.auth.verifyCode({ email, otp, deviceId, trusted, tenantId: clientId });
      if (res.status === "success" && res.data.token) {
        const clientesDisponibles = res.data.clientes || [];
        setClientes(clientesDisponibles);
        login(res.data.token, clientesDisponibles);
        if (trusted) localStorage.setItem("trusted", "1"); else localStorage.removeItem("trusted");
        if (res.data.rol === 1) { navigate(clientPath('/admin')); return; }
        if (res.data.isNew) setStep(3);
        else if (clientesDisponibles.length > 1) setStep(4);
        else navigate(volverA || `/${clientId}`);
      } else {
        setError(res.message || "Código incorrecto o expirado.");
      }
    } catch { setError("Error de red o servidor."); }
    setLoading(false);
  };

  const handleCompleteProfile = async () => {
    setLoading(true); setError("");
    try {
      const res = await api.usuario.editUsuario({ nombre, apellido, telefono });
      if (res.status === "success") {
        const userRes = await api.usuario.getCurrentUser();
        if (userRes.status === "success") setUser(userRes.data.usuario);
        navigate(volverA || `/${clientId}`);
      } else { setError(res.message || "Error al actualizar los datos."); }
    } catch { setError("Error de red o servidor."); }
    setLoading(false);
  };

  const handleResendCode = () => {
    if (resendTimer > 0) return;
    setIsResending(true); setError(""); setCaptchaToken(""); setCaptchaKey(k => k + 1);
  };

  return (
    <div className="login-container" style={loginBg ? { '--login-bg-url': `url(${loginBg})` } : undefined}>
      <div style={{ backgroundColor: '#fff', borderRadius: 15, boxShadow: '0 8px 32px #000', width: '90%', maxWidth: 400, padding: 32 }}>
        {loginLogo && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
            <img src={loginLogo} height="50px" alt="Logo" style={{ objectFit: 'contain' }} />
          </div>
        )}

        {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

        {/* PASO 1 */}
        {step === 1 && (
          <Form layout="vertical" onFinish={handleSendCode}>
            <Title level={5} style={{ textAlign: 'center' }}>Ingresá tu dirección de correo electrónico.</Title>
            <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 16 }}>
              Te vamos a enviar un código para confirmar tu identidad.
            </Text>
            <Form.Item name="email" rules={[{ required: true, type: 'email', message: 'Ingresá un correo válido' }]}>
              <Input size="large" placeholder="Correo electrónico" type="email"
                value={email} onChange={(e) => setEmail(e.target.value)} />
            </Form.Item>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <Turnstile key={captchaKey} ref={captchaRef} sitekey={TURNSTILE_SITEKEY}
                onVerify={async (token) => {
                  setCaptchaToken(token);
                  if (isResending) {
                    setLoading(true);
                    try {
                      const res = await api.auth.sendCode({ email, deviceId, captcha: token, tenantId: clientId });
                      if (res.status === "success") { setResendTimer(60); setStep(2); }
                      else setError(res.message || "No se pudo reenviar el código.");
                    } catch { setError("Error de red o servidor."); }
                    setLoading(false); setIsResending(false);
                  }
                }}
              />
            </div>
            <Button type="primary" htmlType="submit" block size="large" loading={loading}
              disabled={!email || !captchaToken}
              style={{ background: 'var(--color-primary)', borderColor: 'var(--color-primary)' }}>
              Enviar código
            </Button>
          </Form>
        )}

        {/* PASO 2 */}
        {step === 2 && (
          <Form layout="vertical" onFinish={handleVerifyCode}>
            <Title level={5} style={{ textAlign: 'center' }}>Revisá tu correo y escribí el código recibido.</Title>
            <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 16 }}>
              Te enviamos un código a <strong>{email}</strong>.
            </Text>
            <Form.Item name="otp" rules={[{ required: true, message: 'Ingresá el código' }]}>
              <Input size="large" placeholder="Código" maxLength={6}
                value={otp} onChange={(e) => setOtp(e.target.value)} />
            </Form.Item>
            <Form.Item style={{ marginBottom: 8 }}>
              <Checkbox checked={trusted} onChange={(e) => setTrusted(e.target.checked)}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  Confiar en este dispositivo
                  <Tooltip title="Si marcás esta opción, tu sesión se mantendrá iniciada por más tiempo en este dispositivo.">
                    <QuestionCircleOutlined style={{ color: '#aaa', cursor: 'pointer' }} />
                  </Tooltip>
                </span>
              </Checkbox>
            </Form.Item>
            <Button type="primary" htmlType="submit" block size="large" loading={loading}
              style={{ background: 'var(--color-primary)', borderColor: 'var(--color-primary)', marginBottom: 8 }}>
              Iniciar sesión
            </Button>
            <Button type="text" block onClick={handleResendCode} disabled={loading || resendTimer > 0}>
              {resendTimer > 0 ? `Reenviar código (${resendTimer}s)` : "Reenviar código"}
            </Button>
            {isResending && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                <Turnstile key={captchaKey} ref={captchaRef} sitekey={TURNSTILE_SITEKEY}
                  onVerify={async (token) => {
                    setCaptchaToken(token); setLoading(true);
                    try {
                      const res = await api.auth.sendCode({ email, deviceId, captcha: token, tenantId: clientId });
                      if (res.status === "success") { setResendTimer(60); setIsResending(false); }
                      else setError(res.message || "No se pudo reenviar el código.");
                    } catch { setError("Error de red o servidor."); }
                    setLoading(false);
                  }}
                />
              </div>
            )}
          </Form>
        )}

        {/* PASO 3 — Completar perfil */}
        {step === 3 && (
          <Form layout="vertical" onFinish={handleCompleteProfile}>
            <Title level={5} style={{ textAlign: 'center' }}>¡Bienvenido!</Title>
            <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 16 }}>
              Completá tus datos para continuar.
            </Text>
            <Form.Item label="Nombre" name="nombre" rules={[{ required: true, message: 'Ingresá tu nombre' }]}>
              <Input size="large" value={nombre} onChange={(e) => setNombre(e.target.value)} />
            </Form.Item>
            <Form.Item label="Apellido" name="apellido" rules={[{ required: true, message: 'Ingresá tu apellido' }]}>
              <Input size="large" value={apellido} onChange={(e) => setApellido(e.target.value)} />
            </Form.Item>
            <Form.Item label="Teléfono (opcional)">
              <Input size="large" value={telefono} onChange={(e) => setTelefono(e.target.value)} />
            </Form.Item>
            <Button type="primary" htmlType="submit" block size="large" loading={loading}
              style={{ background: 'var(--color-primary)', borderColor: 'var(--color-primary)' }}>
              Continuar
            </Button>
          </Form>
        )}

        {/* PASO 4 — Seleccionar cliente */}
        {step === 4 && (
          <div>
            <Title level={5} style={{ textAlign: 'center' }}>Seleccioná un cliente</Title>
            <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 16 }}>
              Tenés acceso a múltiples clientes.
            </Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {clientes.map(cliente => (
                <Button key={cliente.id} block size="large"
                  onClick={async () => { await selectCliente(cliente.id, null, cliente); navigate(volverA || `/${clientId}`); }}>
                  {cliente.nombre || `Cliente ${cliente.id}`}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
